import type {
  FrameDriver,
  FrameDriverCallback,
  FrameDriverErrorHandler,
  FrameDriverMode,
  FrameDriverState,
} from '@/graphics/FrameDriver'
import { normalizeFrameDriverError } from '@/graphics/FrameDriver'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('graphics:frame:webgl')

export interface WebGLFrameDriverOptions {
  readonly mode: FrameDriverMode
  readonly onError?: FrameDriverErrorHandler
  readonly requestAnimationFrame?: typeof globalThis.requestAnimationFrame
  readonly cancelAnimationFrame?: typeof globalThis.cancelAnimationFrame
}

/** WebGL2 的唯一 window RAF owner。 */
export class WebGLFrameDriver implements FrameDriver {
  readonly mode: FrameDriverMode

  private readonly onError?: FrameDriverErrorHandler
  private readonly requestFrame: typeof globalThis.requestAnimationFrame
  private readonly cancelFrame: typeof globalThis.cancelAnimationFrame
  private callback: FrameDriverCallback | null = null
  private running = false
  private disposed = false
  private pendingInvalidation = false
  private rafId: number | null = null
  private generation = 0

  constructor(options: WebGLFrameDriverOptions) {
    this.mode = options.mode
    this.onError = options.onError
    this.requestFrame =
      options.requestAnimationFrame ?? ((callback) => globalThis.requestAnimationFrame(callback))
    this.cancelFrame = options.cancelAnimationFrame ?? ((id) => globalThis.cancelAnimationFrame(id))
  }

  get state(): FrameDriverState {
    if (this.disposed) return 'disposed'
    if (!this.running) return 'stopped'
    if (this.mode === 'on-demand' && this.rafId === null) return 'idle'
    return 'running'
  }

  get scheduled(): boolean {
    return this.rafId !== null
  }

  start(callback: FrameDriverCallback): void {
    if (this.disposed) return
    this.callback = callback
    if (this.running) return
    this.running = true
    this.generation++
    if (this.mode === 'continuous' || this.pendingInvalidation) {
      this.arm()
    }
  }

  stop(): void {
    if (this.disposed || (!this.running && this.rafId === null)) return
    this.running = false
    this.pendingInvalidation = false
    this.generation++
    if (this.rafId !== null) {
      this.cancelFrame(this.rafId)
      this.rafId = null
    }
  }

  invalidate(): void {
    if (this.disposed) return
    this.pendingInvalidation = true
    if (this.running && this.mode === 'on-demand') {
      this.arm()
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.stop()
    this.callback = null
    this.disposed = true
  }

  private arm(): void {
    if (!this.running || this.disposed || this.rafId !== null || !this.callback) return
    const generation = this.generation
    this.rafId = this.requestFrame((timestamp) => {
      this.rafId = null
      if (!this.running || this.disposed || generation !== this.generation || !this.callback) {
        return
      }
      if (this.mode === 'on-demand') {
        this.pendingInvalidation = false
      }
      try {
        this.callback(timestamp)
      } catch (error) {
        this.reportError(error, 'tick')
      }
      if (this.mode === 'continuous') {
        this.arm()
      } else if (this.pendingInvalidation) {
        this.arm()
      }
    })
  }

  private reportError(error: unknown, phase: string): void {
    const normalized = normalizeFrameDriverError(error)
    if (this.onError) {
      this.onError(normalized, phase)
      return
    }
    log.error(`event=frameDriver.error phase=${phase} errorName=${normalized.name}`)
  }
}
